import { nextTick } from "vue";
import { useRoute } from "vue-router";

import { history } from "@/utils/history";
import { scrolling, type Scrolling } from "@/utils/scrolling";

/*
 * This composable provides components the ability to register
 * themselves as containing scrollable containers for a given route.
 *
 * As a result, the router knows which scrollable containers to use to save
 * and restore the scrolling positions when navigating between routes.
 *
 * Note that the scrolling positions capturing process is performed globally
 * by the navigation system (before navigating away to another route).
 *
 * However, the restoration is taken care on a per-component basis by the
 * useScrollable composable.
 */

type Mode = "automatic" | "manual";

export function useScrollable(
  containerIds: string[],
  mode: Mode = "automatic"
) {
  const route = useRoute();

  const restore = async () => {
    // Restore the scrolling position for the current route if it exists
    const current = history.current();
    if (current && route?.path && current.path === route.path) {
      if (current.positions) {
        // Get the scrolling positions for all the containers but only keep the
        // ones that relate to this component i.e are in the containerIds array.
        //
        // The other will be restored by the other components's useScrollable.
        const positions = Array.from(current.positions.values())
          .flat()
          .filter((position: Scrolling.Position) =>
            containerIds.includes(position.containerId)
          );
        if (positions.length > 0) {
          // Wait times in milliseconds
          const retries = [0, 100, 200, 500];
          for (let i = 0; i < retries.length; i++) {
            // Attempt scroll restoration
            scrolling.restore(positions);
            // Wait for the next DOM update cycle to ensure the effect took place
            await nextTick();
            // Check if the positions were restored
            const areRestored = containerIds.every((id) => {
              // Window scroll is always successful
              if (id === "window") return true;
              const element = document.getElementById(id);
              if (!element) return false;
              const position = positions.find((p) => p.containerId === id);
              if (!position) return false;
              const x = element.scrollLeft;
              const y = element.scrollTop;
              const isRestored =
                Math.abs(x - position.x) <= 1 && Math.abs(y - position.y) <= 1;
              return isRestored;
            });
            if (areRestored) {
              // Scroll restoration was successful
              break;
            } else if (i === retries.length - 1) {
              // Last attempt failed
              window.console.warn("Unable to restore scroll positions");
            }
            // Wait a bit before the next attempt
            const timeout = retries[i];
            if (timeout > 0) {
              await new Promise((resolve) => setTimeout(resolve, timeout));
            }
          }
        }
      }
    }
  };

  const mount = async () => {
    // Register the containerIDs for this route so the system knows
    // which containers to use to save and restore the scrolling position.
    scrolling.register(route, containerIds);

    if (mode === "automatic") {
      restore();
    }
  };

  const unmount = () => {
    // Unregister the container IDs for this route to prevent potential conflicts,
    // though unlikely.
    scrolling.unregister(route);
  };

  return {
    mount,
    unmount,
    restore,
  };
}
